// src/components/SuperAdmin/TenantForm.jsx
import React, { useState } from 'react';
import { superAdminService } from '../../services/api';
import { toast } from 'react-hot-toast';

export default function TenantForm({ tenant, onSuccess, onCancel }) {
  // Calculate default expiry date (1 year from now)
  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split('T')[0];
  };

  // Generate suggested tenant code from business name
  const generateSuggestedCode = (businessName) => {
    if (!businessName) return '';
    const prefix = businessName.substring(0, 3).toUpperCase();
    const randomNum = Math.floor(Math.random() * 900 + 100);
    return `${prefix}${randomNum}`;
  };

  const [formData, setFormData] = useState({
    business_name: tenant?.business_name || '',
    email: tenant?.email || '',
    phone: tenant?.phone || '',
    address: tenant?.address || '',
    tin_number: tenant?.tin_number || '',
    tenant_code: tenant?.tenant_code || '',
    subscription_plan: tenant?.subscription_plan || 'basic',
    subscription_expiry: tenant?.subscription_expiry || getDefaultExpiryDate(),
    // Admin user fields (only for new tenant)
    admin_username: '',
    admin_password: '',
    admin_full_name: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [suggestedCode, setSuggestedCode] = useState('');
  const isEditMode = !!tenant;

  // Update suggested code when business name changes (for new tenant)
  const handleBusinessNameChange = (e) => {
    const name = e.target.value;
    setFormData({ ...formData, business_name: name });
    if (!isEditMode && name) {
      const suggestion = generateSuggestedCode(name);
      setSuggestedCode(suggestion);
    }
  };

  const applySuggestedCode = () => {
    if (suggestedCode) {
      setFormData({ ...formData, tenant_code: suggestedCode });
      setSuggestedCode('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate tenant code for new tenant
    if (!isEditMode && !formData.tenant_code) {
      toast.error('Tenant code is required for login');
      return;
    }
    
    // Validate tenant code format (alphanumeric, 4-10 characters)
    if (!isEditMode && !/^[A-Z0-9]{4,10}$/.test(formData.tenant_code.toUpperCase())) {
      toast.error('Tenant code must be 4-10 alphanumeric characters (uppercase)');
      return;
    }
    
    // Validate passwords match for new tenant
    if (!isEditMode && formData.admin_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    // Validate admin details for new tenant
    if (!isEditMode && (!formData.admin_username || !formData.admin_password || !formData.admin_full_name)) {
      toast.error('Admin user details are required');
      return;
    }
    
    setLoading(true);
    try {
      const submitData = {
        business_name: formData.business_name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address || null,
        tin_number: formData.tin_number || null,
        tenant_code: formData.tenant_code.toUpperCase(),
        subscription_plan: formData.subscription_plan,
        subscription_expiry: formData.subscription_expiry || null,
      };
      
      // Add admin details only for new tenant creation
      if (!isEditMode) {
        submitData.admin_username = formData.admin_username;
        submitData.admin_password = formData.admin_password;
        submitData.admin_full_name = formData.admin_full_name;
      }
      
      if (isEditMode) {
        await superAdminService.updateTenant(tenant.id, submitData);
        toast.success('Tenant updated successfully');
      } else {
        const response = await superAdminService.createTenant(submitData);
        toast.success(`Tenant created! Login code: ${response.tenant_code}`);
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Tenant Information Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
          Business Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.business_name}
              onChange={handleBusinessNameChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="Enter business name"
              disabled={isEditMode}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tenant Code <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={formData.tenant_code}
                onChange={(e) => setFormData({ ...formData, tenant_code: e.target.value.toUpperCase() })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                placeholder="e.g., TECH001"
                required={!isEditMode}
                disabled={isEditMode}
              />
              {!isEditMode && suggestedCode && (
                <button
                  type="button"
                  onClick={applySuggestedCode}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm whitespace-nowrap"
                >
                  Use Suggested: {suggestedCode}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This code will be used for login. Example: TECH001
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="admin@business.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="0712345678"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="2"
              placeholder="Business address (optional)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              TIN/PIN Number
            </label>
            <input
              type="text"
              value={formData.tin_number}
              onChange={(e) => setFormData({ ...formData, tin_number: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="A123456789Z"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Plan
            </label>
            <select
              value={formData.subscription_plan}
              onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="basic">Basic - KES 2,999/month</option>
              <option value="premium">Premium - KES 5,999/month</option>
              <option value="enterprise">Enterprise - KES 12,999/month</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Expiry
            </label>
            <input
              type="date"
              value={formData.subscription_expiry}
              onChange={(e) => setFormData({ ...formData, subscription_expiry: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Default: 1 year from today
            </p>
          </div>
        </div>
      </div>

      {/* Admin User Section - Only show for new tenant creation */}
      {!isEditMode && (
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
            Administrator Account
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.admin_full_name}
                onChange={(e) => setFormData({ ...formData, admin_full_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.admin_username}
                onChange={(e) => setFormData({ ...formData, admin_username: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                placeholder="admin_username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={formData.admin_password}
                onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                placeholder="********"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                placeholder="********"
              />
            </div>
          </div>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Login Info:</strong> Use Tenant Code: <strong>{formData.tenant_code || '[Your Code]'}</strong> with admin credentials
            </p>
          </div>
        </div>
      )}

      {/* Edit Mode Info */}
      {isEditMode && (
        <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
          <p className="text-xs text-yellow-700">
            <strong>Note:</strong> Tenant code cannot be changed after creation. To change admin password, use User Management in Settings.
          </p>
        </div>
      )}

      <div className="flex space-x-3 pt-4">
        <button 
          type="submit" 
          disabled={loading} 
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            isEditMode ? 'Update Tenant' : 'Create Tenant'
          )}
        </button>
        <button 
          type="button" 
          onClick={onCancel} 
          className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}